import React from "react";
import { Navigate } from "react-router-dom";
import { checkModuleAccess, checkPermission, checkRole } from "@/lib/utils";

interface PermissionGuardProps {
  /** The permission module (e.g., "banner", "events") — user needs ANY permission in this module */
  requiredModule?: string;
  /** Specific permission (e.g., "events:create") — user needs this exact permission */
  permissionName?: string;
  /** Required role (e.g., "SUPER_ADMIN") — user needs this exact role */
  requiredRole?: string;
  /** Where to redirect when permission is denied */
  redirectTo?: string;
  children: React.ReactNode;
}

/**
 * Wraps a route element and redirects to dashboard (or custom path)
 * if the user lacks the required permission/module access/role.
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
  requiredModule,
  permissionName,
  requiredRole,
  redirectTo = "/dashboard",
  children,
}) => {
  // Check role first
  if (requiredRole && !checkRole(requiredRole)) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check module-level access
  if (requiredModule && !checkModuleAccess(requiredModule)) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check specific permission
  if (permissionName && !checkPermission(permissionName)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default PermissionGuard;
